import { Image } from 'expo-image';
import * as Updates from 'expo-updates';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { supabase } from '@/lib/supabase';
import { useOnAppActive } from './useOnAppActive';

/** Απορρίπτει με timeout αντί να μένει κρεμασμένο για πάντα αν κολλήσει I/O. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

/**
 * Ενεργός έλεγχος για νέο EAS Update (OTA). Χωρίς αυτό, το expo-updates
 * ελέγχει μόνο σε πλήρες cold start και εφαρμόζει το update ένα restart
 * αργότερα — πολλοί χρήστες που απλώς βάζουν την εφαρμογή σε background δεν
 * βλέπουν ποτέ τις νέες εκδόσεις χωρίς να την κλείσουν εντελώς (ή να κάνουν
 * clear cache). Εδώ ελέγχουμε ενεργά στο mount και σε κάθε επαναφορά στο
 * foreground, κατεβάζουμε το update αν υπάρχει, και το εφαρμόζουμε μόνο όταν
 * το επιβεβαιώσει ο χρήστης μέσω του UpdatePrompt (reloadAsync).
 */
export function useOtaUpdate() {
  const [ready, setReady] = useState(false);
  const [changelog, setChangelog] = useState<string | null>(null);
  const checkingRef = useRef(false);
  const reloadingRef = useRef(false);

  const check = useCallback(async () => {
    if (Platform.OS === 'web' || __DEV__ || !Updates.isEnabled) return;
    // Το mount effect και το app-active listener μπορούν να πυροδοτηθούν σχεδόν
    // ταυτόχρονα· δύο ταυτόχρονα fetchUpdateAsync() έχουν προκαλέσει κολλημένο
    // reload σε κάποιες συσκευές, οπότε επιτρέπουμε μόνο ένα τη φορά.
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const result = await Updates.checkForUpdateAsync();
      if (result.isAvailable) {
        await Updates.fetchUpdateAsync();
        void supabase
          .from('app_config')
          .select('value')
          .eq('key', 'ota_changelog')
          .maybeSingle()
          .then(({ data }) => setChangelog(data?.value?.trim() || null));
        setReady(true);
      }
    } catch {
      // σιωπηλά — π.χ. χωρίς σύνδεση
    } finally {
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => { void check(); }, [check]);
  useOnAppActive(() => void check());

  const applyUpdate = useCallback(async () => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    try {
      // Σκόπιμα ΔΕΝ διαγράφουμε ολόκληρο το FileSystem.cacheDirectory εδώ — εκεί
      // αντιγράφονται και τα bundled font/icon assets (π.χ. Ionicons) κατά το
      // πρώτο load, και μια γενική διαγραφή τα άφηνε λείπουν μέχρι πλήρες cold
      // start. Καθαρίζουμε μόνο την (καλά-καθορισμένη) cache του expo-image.
      await withTimeout(
        Promise.all([
          Image.clearDiskCache(),
          Image.clearMemoryCache(),
        ]),
        3000,
      );
    } catch {
      // αγνόησε (ή timeout) — προτεραιότητα να γίνει το reload ούτως ή άλλως
    }
    await Updates.reloadAsync();
  }, []);

  return { ready, changelog, applyUpdate };
}
